import React, { useState } from 'react';
import { Card, List, Typography, Alert, Select, Space, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/axios';

const { Title, Paragraph } = Typography;
const { Option } = Select;

interface WeatherAlert {
  id: string;
  type: 'WARNING' | 'WATCH' | 'ADVISORY';
  severity: 'EXTREME' | 'SEVERE' | 'MODERATE' | 'MINOR';
  title: string;
  description: string;
  area: string;
  startTime: string;
  endTime: string;
  source: string;
}

interface StormInfo {
  id: string;
  name: string;
  type: string;
  status: 'ACTIVE' | 'DISSIPATED';
  location: string;
  intensity: string;
  movement: string;
  lastUpdate: string;
}

const WeatherFeeds: React.FC = () => {
  const [selectedArea, setSelectedArea] = useState<string>('all');

  const { data: alertsData, isLoading: alertsLoading, error: alertsError } = useQuery<{ success: boolean; data: WeatherAlert[] }>({
    queryKey: ['weather-alerts', selectedArea],
    queryFn: async () => {
      // For now, return mock data since we don't have a real weather API
      // In a real implementation, this would call a weather API
      const mockAlerts = [
        {
          id: '1',
          type: 'WARNING' as const,
          severity: 'SEVERE' as const,
          title: 'Severe Thunderstorm Warning',
          description: 'Severe thunderstorms expected in the area with possible hail and strong winds.',
          area: 'Northeast',
          startTime: new Date().toISOString(),
          endTime: new Date(Date.now() + 3600000).toISOString(),
          source: 'NOAA',
        },
        {
          id: '2',
          type: 'WATCH' as const,
          severity: 'MODERATE' as const,
          title: 'Flood Watch',
          description: 'Heavy rainfall may cause flooding in low-lying areas.',
          area: 'Southeast',
          startTime: new Date().toISOString(),
          endTime: new Date(Date.now() + 7200000).toISOString(),
          source: 'NOAA',
        },
      ];

      const filteredAlerts = selectedArea === 'all' 
        ? mockAlerts 
        : mockAlerts.filter(alert => alert.area.toLowerCase() === selectedArea);

      return { success: true, data: filteredAlerts };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: stormsData, isLoading: stormsLoading, error: stormsError } = useQuery<{ success: boolean; data: StormInfo[] }>({
    queryKey: ['storm-info'],
    queryFn: async () => {
      // For now, return mock data since we don't have a real storm API
      // In a real implementation, this would call a storm tracking API
      const mockStorms = [
        {
          id: '1',
          name: 'Tropical Storm Alpha',
          type: 'Tropical Storm',
          status: 'ACTIVE' as const,
          location: 'Atlantic Ocean',
          intensity: 'Category 1',
          movement: 'Northwest at 15 mph',
          lastUpdate: new Date().toISOString(),
        },
        {
          id: '2',
          name: 'Hurricane Beta',
          type: 'Hurricane',
          status: 'ACTIVE' as const,
          location: 'Gulf of Mexico',
          intensity: 'Category 3',
          movement: 'North at 10 mph',
          lastUpdate: new Date().toISOString(),
        },
      ];

      return { success: true, data: mockStorms };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const alerts = alertsData?.data || [];
  const storms = stormsData?.data || [];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'EXTREME':
        return 'red';
      case 'SEVERE':
        return 'orange';
      case 'MODERATE':
        return 'yellow';
      case 'MINOR':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getAlertTypeColor = (type: string) => {
    switch (type) {
      case 'WARNING':
        return 'red';
      case 'WATCH':
        return 'orange';
      case 'ADVISORY':
        return 'blue';
      default:
        return 'default';
    }
  };

  if (alertsError || stormsError) {
    return <Alert type="error" message="Failed to load weather information" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Title level={4}>Weather Alerts</Title>
        <Select
          defaultValue="all"
          style={{ width: 200 }}
          onChange={setSelectedArea}
        >
          <Option value="all">All Areas</Option>
          <Option value="northeast">Northeast</Option>
          <Option value="southeast">Southeast</Option>
          <Option value="midwest">Midwest</Option>
          <Option value="southwest">Southwest</Option>
          <Option value="northwest">Northwest</Option>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Active Alerts" loading={alertsLoading}>
          {alerts.length > 0 ? (
            <List
              dataSource={alerts}
              renderItem={(alert: WeatherAlert) => (
                <List.Item>
                  <div className="w-full">
                    <Space direction="vertical" size="small" className="w-full">
                      <div className="flex justify-between items-start">
                        <Title level={5}>{alert.title}</Title>
                        <Space>
                          <Tag color={getAlertTypeColor(alert.type)}>{alert.type}</Tag>
                          <Tag color={getSeverityColor(alert.severity)}>{alert.severity}</Tag>
                        </Space>
                      </div>
                      <Paragraph>{alert.description}</Paragraph>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Area: {alert.area}</span>
                        <span>Source: {alert.source}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Start: {new Date(alert.startTime).toLocaleString()}</span>
                        <span>End: {new Date(alert.endTime).toLocaleString()}</span>
                      </div>
                    </Space>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div className="text-center py-4 text-gray-500">
              No active weather alerts for the selected area.
            </div>
          )}
        </Card>

        <Card title="Storm Information" loading={stormsLoading}>
          {storms.length > 0 ? (
            <List
              dataSource={storms}
              renderItem={(storm: StormInfo) => (
                <List.Item>
                  <div className="w-full">
                    <Space direction="vertical" size="small" className="w-full">
                      <div className="flex justify-between items-start">
                        <Title level={5}>{storm.name}</Title>
                        <Tag color={storm.status === 'ACTIVE' ? 'red' : 'blue'}>
                          {storm.status}
                        </Tag>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="font-semibold">Type:</span> {storm.type}
                        </div>
                        <div>
                          <span className="font-semibold">Intensity:</span> {storm.intensity}
                        </div>
                        <div>
                          <span className="font-semibold">Location:</span> {storm.location}
                        </div>
                        <div>
                          <span className="font-semibold">Movement:</span> {storm.movement}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        Last Update: {new Date(storm.lastUpdate).toLocaleString()}
                      </div>
                    </Space>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div className="text-center py-4 text-gray-500">
              No active storms currently being tracked.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default WeatherFeeds; 